import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import throttle from 'lodash.throttle';
import { elementScroll, useVirtualizer } from '@tanstack/react-virtual';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import RegionPicker from './RegionPicker';
import { fetchPosition, getIsPositionPending, getPosition, getPositionError, getRegion, } from '../store/slices/locationSlice';
import { getSelectedStore, resetSelectedStore as resetSelectedStoreReducer } from '../store/slices/storesSlice';
import StoreDetails from './StoreDetails';
import { MediaQueries } from '../types/media-queries';
import { useGetStoresQuery } from '../store/services/stores';
import { getFilteredStores } from '../store/selectors/storesSelectors';
import StoreListItem from './StoreListItem';
import { innerHeight, maxInnerHeight, outerHeight } from '../../../utils/dom';
const StoreList = ({ askForPosition = true, headerComponent, footerComponent }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const position = useSelector(getPosition);
    const positionError = useSelector(getPositionError);
    const region = useSelector(getRegion);
    const selectedStore = useSelector(getSelectedStore);
    const isPositionPending = useSelector(getIsPositionPending);
    const stores = useSelector(getFilteredStores);
    const isDesktopScreen = useMediaQuery({
        query: `(min-width: ${MediaQueries.SM}px)`,
    });
    const { isLoading: isStoresPending } = useGetStoresQuery('');
    const listContainerRef = useRef(null);
    const virtualListRef = useRef(null);
    const elRef = useRef(null);
    const scrollingRef = React.useRef(null);
    const loaderRef = useRef(null);
    const [parentHeight, setParentHeight] = useState(0);
    const [childrenHeight, setChildrenHeight] = useState(0);
    const showStoreDetails = selectedStore && !isDesktopScreen;
    const decreaseValueByOne = (val) => val - 1;
    const easeInOutQuint = (t) => {
        return t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * decreaseValueByOne(t) * t * t * t * t;
    };
    const scrollToFn = React.useCallback((offset, canSmooth, instance) => {
        if (!virtualListRef.current) {
            return;
        }
        const duration = 1000;
        const start = virtualListRef.current.scrollTop;
        scrollingRef.current = Date.now();
        const startTime = Date.now();
        const run = () => {
            if (scrollingRef.current !== startTime)
                return;
            const now = Date.now();
            const elapsed = now - startTime;
            const progress = easeInOutQuint(Math.min(elapsed / duration, 1));
            const interpolated = start + (offset - start) * progress;
            if (elapsed < duration) {
                elementScroll(interpolated, canSmooth, instance);
                requestAnimationFrame(run);
            }
            else {
                elementScroll(interpolated, canSmooth, instance);
            }
        };
        requestAnimationFrame(run);
    }, []);
    const virtualizer = useVirtualizer({
        count: stores.length,
        getScrollElement: () => virtualListRef.current,
        estimateSize: () => 125,
        overscan: 5,
        scrollToFn,
    });
    const items = virtualizer.getVirtualItems();
    const resetSelectedStore = () => {
        dispatch(resetSelectedStoreReducer());
    };
    const listMaxHeight = `${Math.floor(parentHeight - childrenHeight)}px`;
    const scrollToStore = (store) => {
        if (!virtualListRef.current) {
            return;
        }
        const index = stores.findIndex(item => store.id === item.id);
        virtualizer.scrollToIndex(index);
    };
    const resizeHandler = throttle(() => {
        setParentHeight(Math.max(maxInnerHeight(elRef.current.parentElement.parentElement), innerHeight(elRef.current.parentElement.parentElement)));
        setChildrenHeight(Array.from(elRef.current.children)
            .filter(el => el !== listContainerRef.current && el !== loaderRef.current)
            .reduce((height, el) => height + outerHeight(el), 0));
    }, 300);
    useEffect(() => {
        setTimeout(() => {
            resizeHandler();
            window.addEventListener('resize', resizeHandler);
        }, 300);
        if (askForPosition && !position && !region) {
            // @ts-ignore
            dispatch(fetchPosition());
        }
        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    }, []);
    useEffect(() => {
        setTimeout(() => {
            resizeHandler();
        }, 300);
    }, [stores, showStoreDetails, position, region]);
    useEffect(() => {
        if (selectedStore) {
            if (virtualListRef) {
                scrollToStore(selectedStore);
            }
        }
    }, [selectedStore]);
    return (_jsxs("div", { ref: elRef, children: [(!askForPosition || position || region) && (_jsxs(_Fragment, { children: [showStoreDetails && selectedStore && (_jsx(StoreDetails, { store: selectedStore, showBack: true, back: resetSelectedStore })), !showStoreDetails && (_jsxs(_Fragment, { children: [headerComponent, isStoresPending && (_jsx("div", { className: "text-center py-5", ref: loaderRef, children: _jsx("div", { className: "spinner-border spinner-border-lg", role: "status", children: _jsx("span", { className: "sr-only", children: t('store_locator.loading') }) }) })), stores.length > 0 && !isStoresPending && (_jsx("div", { ref: listContainerRef, className: "map-content-inner-no-gutters", children: _jsx("div", { ref: virtualListRef, className: "d-block list-group list-group-padless list-group-padded-md", style: {
                                        maxHeight: listMaxHeight,
                                        overflowY: 'auto',
                                    }, children: _jsx("div", { style: {
                                            height: `${virtualizer.getTotalSize()}px`,
                                            width: '100%',
                                            position: 'relative',
                                        }, children: items.map(virtualRow => (_jsx("div", { "data-index": virtualRow.index, ref: virtualizer.measureElement, style: {
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                transform: `translateY(${virtualRow.start}px)`,
                                            }, children: _jsx(StoreListItem, { source: stores[virtualRow.index] }) }, virtualRow.index))) }) }) })), !isStoresPending && !stores.length && _jsx("div", { children: t('store_locator.no_stores_found') }), footerComponent] }))] })), isPositionPending && (_jsx("div", { className: "position-absolute absolute-center py-5", children: _jsx("div", { className: "spinner-border spinner-border-lg", role: "status", children: _jsx("span", { className: "sr-only", children: t('store_locator.loading') }) }) })), positionError && !region && _jsx(RegionPicker, {})] }));
};
export default StoreList;
